import {useRef, useState} from 'react'
import PropTypes from 'prop-types'
import './styles.scss'
import '../../styles/index.scss'
import './fileInputStyles.scss'
import { observer} from 'mobx-react'
import Icon from '../Icon/Icon'
import { ACCEPTANCE_CONDITIONS } from '../../constants'
import { acceptedMimeTypes, getFilesTotalSize, isValidFileType } from '../../utils/files'

const getEstimatedSize = (currentFiles = [], newFiles = [], newFile) => {
  const currentTotalSize = getFilesTotalSize(currentFiles)
  const newFilesTotalSize = getFilesTotalSize(newFiles)
  return currentTotalSize + newFilesTotalSize + newFile.size
}

const getFilesAndErrors = (receivedFiles, currentFiles) => {
  const { limitSize, limitCount } = ACCEPTANCE_CONDITIONS
  const totalCountFiles = currentFiles.length + receivedFiles.length
  const files = []
  let errors = []
  if (totalCountFiles > limitCount) {
    errors.push({
      reason: `Failed to upload because the total file count exceeds ${limitCount}.`,
    })
  } else {
    for (const file of receivedFiles) {
      const fileExtension = file.name.split('.').pop()
      if (file.size > limitSize) {
        errors.push({
          name: file.name,
          reason: `${file.name} failed to upload because the total file size exceeds 18 MB.`,
        })
      } else if (!isValidFileType(fileExtension)) {
        errors.push({
          name: file.name,
          reason: `${file.name} failed to upload because "${fileExtension}" is not a supported file format.`,
        })
      } else if (getEstimatedSize(currentFiles, files, file) > limitSize) {
        errors.push({
          name: file.name,
          reason: `${file.name} failed to upload because the total file size exceeds 18 MB.`,
        })
      } else {
        files.push(file)
      }
    }
  }
  return {errors, files}
}

const Attachment = ({ name, size, onDelete }) => {
  const [isHovered, setIsHovered] = useState(false)
  const fileSize = (size / 1000).toFixed(2)
  const fileUnit = fileSize < 1024 ? 'KB' : 'MB'
  return (
    <div
      className='attachedFileContainer'
      onMouseOver={() => setIsHovered(true)}
      onMouseOut={() => setIsHovered(false)}
    >
      <div className='centerItems'>
        <Icon name='attached-document' />
        <p>{name}</p>
      </div>
      <div className='centerItems'>
        <span className='fileSize'>{`${fileSize} ${fileUnit}`}</span>
        {!isHovered ? (
          <Icon name='success' width='24' height='24' />
        ) : (
          <Icon
            name='close'
            width='20'
            height='20'
            className='closeButton'
            onClick={onDelete}
          />
        )}
      </div>
    </div>
  )
}

const Error = ({ reason, onDelete }) => {
  return (
    <div className='attachedFileError'>
      {reason}
      <Icon
        name='close'
        width='20'
        height='20'
        className='closeButton'
        onClick={onDelete}
      />
    </div>
  )
}

export const FileInput = ({ automationName, name, Store }) => {
  const { addAttachments, removeAttachment, attachments: currentFiles } = Store
  const [background, setBackground] = useState('')
  const [errors, setErrors] = useState([])
  const dragCounter = useRef(0)
  const fileInputRef = useRef(null)
  const onDrag = (e, isEntering) => {
    e.preventDefault()
    if (isEntering) {
      dragCounter.current++
    } else {
      dragCounter.current--
    }
    if (dragCounter.current > 0) {
      setBackground('uploadBoxBackground')
    } else {
      setBackground('')
    }
  }
  const onDrop = e => {
    e.preventDefault()
    dragCounter.current = 0
    setBackground('')
    onChange(e.dataTransfer.files)
  }
  const onChange = receivedFiles => {
    const { files, errors } = getFilesAndErrors(receivedFiles, currentFiles)
    if (files.length > 0) {
      addAttachments(files)
    }
    if (errors.length > 0) {
      setErrors(errors)
    }
  }
  const deleteError = index => {
    const errorList = errors.filter((_, i) => i !== index)
    setErrors(errorList)
  }

  return (
    <div>
      <div
        className={`form-group form-control uploadBox ${automationName} ${background}`}
        onClick={() => fileInputRef.current?.click()}
        onDrag={e => onDrag(e, true)}
        onDragEnter={e => onDrag(e, true)}
        onDragLeave={e => onDrag(e, false)}
        onDragOver={e => {
          e.stopPropagation()
          e.preventDefault()
        }}
        onDrop={onDrop}
      >
        <Icon name='upload' />
        <h5 className='title'>CLICK OR DRAG AND DROP</h5>
        <p>Maximum file size 18 MB</p>
        <input
          multiple
          accept={acceptedMimeTypes}
          type='file'
          name={name}
          data-automation-name={automationName}
          className='hideElement'
          ref={fileInputRef}
          onChange={e => {
            onChange(e.currentTarget.files)
          }}
        />
      </div>
      {currentFiles.map((file, index) => (
        <Attachment
          key={index}
          name={file.name}
          size={file.size}
          onDelete={() => removeAttachment(index)}
        />
      ))}
      {errors.map((error, index) => (
        <Error
          key={error.name}
          reason={error.reason}
          onDelete={() => {
            deleteError(index)
          }}
        />
      ))}
    </div>
  )
}

FileInput.propTypes = {
  name: PropTypes.string.isRequired,
  automationName: PropTypes.string.isRequired,
  Store: PropTypes.object.isRequired,
}
export default observer(FileInput)
